import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: left;
  text-align: left;
  margin: 150px 0;
  padding: 0 50px;

  @media (max-width: 768px) {
    margin: 100px 0;
    padding: 0 30px;
  }

  @media (max-width: 480px) {
    margin: 60px 0;
    padding: 0 20px;
  }

  .section-header {
    margin-bottom: 60px;

    .accent-line {
      font-size: var(--fz-sm);
      letter-spacing: 0.15em;
      color: var(--green);
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 20px;
    }

    h2 {
      font-size: clamp(32px, 8vw, 48px);
      font-weight: 700;
      color: var(--lightest-slate);
      margin: 0 0 20px 0;
      line-height: 1.2;
    }

    p {
      max-width: 650px;
      font-size: var(--fz-lg);
      color: var(--slate);
      line-height: 1.8;
      margin: 0;

      @media (max-width: 768px) {
        font-size: var(--fz-md);
      }
    }
  }

  .archive-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 15px;
    position: relative;
    margin-top: 10px;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }

  p,
  li {
    white-space: normal;
    word-spacing: normal;
    font-size: clamp(14px, 1.5vw, 18px);
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .project-inner {
        transform: translateY(-8px);
        border-color: var(--green);
      }
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 30px;
    border-radius: var(--border-radius);
    background-color: rgba(17, 34, 51, 0.8);
    border: 1px solid rgba(100, 200, 100, 0.2);
    transition: all 0.3s ease;
    overflow: auto;
  }

  .project-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: 35px;
    width: 100%;

    .folder {
      color: var(--green);
      svg {
        width: 40px;
        height: 40px;
      }
    }

    .project-links {
      display: flex;
      align-items: center;
      margin-right: -10px;
      color: var(--light-slate);

      a {
        ${({ theme }) => theme.mixins.flexCenter};
        padding: 8px;
        transition: all 0.3s ease;

        svg {
          transition: all 0.3s ease;
        }

        &:hover svg {
          color: var(--green);
          transform: translateY(-2px);
        }

        &.external {
          svg {
            width: 22px;
            height: 22px;
            margin-right: 0;
          }
        }

        &.github {
          svg {
            width: 20px;
            height: 20px;
          }
        }
      }
    }
  }

  .project-entity {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
    margin-bottom: 10px;
  }

  h3 {
    margin: 0 0 15px;
    color: var(--lightest-slate);
    font-size: clamp(22px, 5vw, 28px);
    font-weight: 600;
    line-height: 1.3;

    a {
      position: static;

      &:hover,
      &:focus {
        color: var(--green);
      }
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .project-tech {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 10px 0 0;
    padding: 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      color: var(--slate);
      line-height: 1.75;

      &:not(:last-child) {
        margin-right: 15px;
      }

      &:not(:last-child):after {
        display: none;
      }
    }
  }

  .project-links {
    margin-left: -10px;
    color: var(--lightest-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 3;
  const projects = data.projects.edges.filter(({ node }) => node);
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { github, external, title, tech } = frontmatter;

    return (
      <div className="project-inner">
        <header>
          <div className="project-top">
            <div className="folder">
              <Icon name="Folder" />
            </div>
            <div className="project-links">
              {github && (
                <a href={github} aria-label="GitHub Link" target="_blank" rel="noreferrer">
                  <Icon name="GitHub" />
                </a>
              )}
              {external && (
                <a
                  href={external}
                  aria-label="External Link"
                  className="external"
                  target="_blank"
                  rel="noreferrer">
                  <Icon name="External" />
                </a>
              )}
            </div>
          </div>

          <h3 className="project-title">
            <a href={external} target="_blank" rel="noreferrer">
              {title}
            </a>
          </h3>

          <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />
        </header>

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map(techItem => (
                <li key={techItem}>{techItem}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        Projects
      </h2>
      <Link className="inline-link archive-link" to="/archive" ref={revealArchiveLink}>
        view the archive
      </Link>

      <ul className="projects-grid">
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }) => (
                <StyledProject key={node.frontmatter.title}>{projectInner(node)}</StyledProject>
              ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {projectsToShow &&
              projectsToShow.map(({ node }) => (
                <CSSTransition
                  key={node.frontmatter.title}
                  classNames="fadeup"
                  timeout={300}
                  exit={false}>
                  <StyledProject
                    key={node.frontmatter.title}
                    ref={el => {
                      if (el) {
                        revealProjects.current.push(el);
                      }
                    }}
                    style={{ transitionDelay: `0ms` }}>
                    {projectInner(node)}
                  </StyledProject>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </ul>

      <button className="more-button" onClick={() => setShowMore(!showMore)}>
        Show {showMore ? 'Less' : 'More'}
      </button>
    </StyledProjectsSection>
  );
};

export default Projects;
